/**
 * Session Control Tab
 * Unified tab view for session management (waiting room, timer, controls)
 * Integrated into UnifiedTherapistDashboard
 */
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import SessionTimer from './SessionTimer';
import SessionControls from './SessionControls';
import PatientWaitingList from './PatientWaitingList';
import { getSessionId } from '../functions';
const useStyles = makeStyles()((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        height: '100%'
    },
    section: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        padding: '16px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#FFFFFF',
        marginBottom: '12px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    sectionIcon: {
        fontSize: '18px'
    },
    sessionInfo: {
        background: 'linear-gradient(135deg, rgba(11, 108, 227, 0.2) 0%, rgba(30, 136, 229, 0.15) 100%)',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '20px',
        border: '1px solid rgba(30, 136, 229, 0.3)'
    },
    sessionInfoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '13px',
        color: 'rgba(255, 255, 255, 0.8)'
    },
    sessionInfoLabel: {
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.6)'
    },
    sessionInfoValue: {
        fontWeight: 600,
        color: '#FFFFFF',
        fontFamily: '"Roboto Mono", monospace'
    },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    statusWaiting: {
        background: 'rgba(255, 193, 7, 0.2)',
        color: '#FFC107',
        border: '1px solid rgba(255, 193, 7, 0.4)'
    },
    statusActive: {
        background: 'rgba(76, 175, 80, 0.2)',
        color: '#4CAF50',
        border: '1px solid rgba(76, 175, 80, 0.4)'
    },
    statusEnded: {
        background: 'rgba(244, 67, 54, 0.2)',
        color: '#F44336',
        border: '1px solid rgba(244, 67, 54, 0.4)'
    }
}));
export default function SessionControlTab({ sessionState, sessionStartTime, lobbyParticipants, onStartSession, onEndSession, onAdmitParticipant, onRejectParticipant }) {
    const { classes, cx } = useStyles();
    const getStatusBadgeClass = () => {
        switch (sessionState) {
            case 'waiting':
                return classes.statusWaiting;
            case 'active':
                return classes.statusActive;
            case 'ended':
                return classes.statusEnded;
            default:
                return classes.statusWaiting;
        }
    };
    const getStatusText = () => {
        switch (sessionState) {
            case 'waiting':
                return 'Waiting';
            case 'active':
                return 'In Progress';
            case 'ended':
                return 'Ended';
            default:
                return 'Unknown';
        }
    };
    return (React.createElement("div", { className: classes.container, "data-testid": "session-control-tab" },
        React.createElement("div", { className: classes.sessionInfo },
            React.createElement("div", { className: classes.sessionInfoRow },
                React.createElement("span", { className: classes.sessionInfoLabel }, "Session ID:"),
                React.createElement("span", { className: classes.sessionInfoValue }, getSessionId())),
            React.createElement("div", { className: classes.sessionInfoRow, style: { marginTop: '8px' } },
                React.createElement("span", { className: classes.sessionInfoLabel }, "Status:"),
                React.createElement("span", { className: cx(classes.statusBadge, getStatusBadgeClass()) }, getStatusText()))),
        React.createElement("div", { className: classes.section },
            React.createElement("div", { className: classes.sectionTitle },
                React.createElement("span", { className: classes.sectionIcon }, "\uD83D\uDEAA"),
                "Waiting Room"),
            React.createElement(PatientWaitingList, { participants: lobbyParticipants, onAdmit: onAdmitParticipant, onReject: onRejectParticipant })),
        React.createElement("div", { className: classes.section },
            React.createElement("div", { className: classes.sectionTitle },
                React.createElement("span", { className: classes.sectionIcon }, "\u23F1\uFE0F"),
                "Session Duration"),
            React.createElement(SessionTimer, { isActive: sessionState === 'active', startTime: sessionStartTime })),
        React.createElement("div", { className: classes.section },
            React.createElement("div", { className: classes.sectionTitle },
                React.createElement("span", { className: classes.sectionIcon }, "\uD83C\uDFAE"),
                "Session Controls"),
            React.createElement(SessionControls, { sessionState: sessionState, onStartSession: onStartSession, onEndSession: onEndSession, disabled: sessionState === 'ended' }))));
}
